import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Box, 
  Button, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
  Fade
} from '@mui/material';
import { 
  Language, 
  Menu as MenuIcon,
  Close
} from '@mui/icons-material';
import { setTheme, setLanguage } from '../../store/actions/uiActions';
import { getTranslation } from '../../i18n';

const ModernHeader = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { theme, language } = useSelector((state) => state.ui);
  const [languageMenu, setLanguageMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const translations = getTranslation(language);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Theme toggle removed - only light theme is used

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenu(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenu(null);
  };

  const handleLanguageChange = (lang) => {
    dispatch(setLanguage(lang));
    localStorage.setItem('language', lang);
    handleLanguageMenuClose();

    // If on landing page, redirect to language-specific landing
    if (location.pathname === '/' || location.pathname === '/en' || location.pathname === '/ru' || location.pathname === '/bg') {
      window.location.href = `/${lang}`;
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Language options
  const languages = [
    { code: 'en', label: 'English' },
    { code: 'ru', label: 'Русский' },
    { code: 'bg', label: 'Български' },
  ];

  // Navigation links
  const navLinks = [
    { name: translations.header.main, path: '/' },
    { name: translations.header.map, path: '/map' },
    { name: translations.header.sensorInfrastructure, path: '/sensor-infrastructure' },
    { name: translations.header.contact, path: '/contact' },
  ];

  return (
    <AppBar 
      position="static" 
      color="primary" 
      elevation={0}
      sx={{
        background: 'linear-gradient(90deg, #1273D1 0%, #0E58A0 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ padding: { xs: 1, sm: 2 }, justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box 
            component={Link} 
            to="/"
            sx={{ 
              textDecoration: 'none',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                opacity: 0.9
              }
            }}
          >
            <Box 
              component="img" 
              src="/images/logo.svg" 
              alt={translations.app.logo}
              sx={{ 
                height: { xs: 40, md: 50 },
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navLinks.map((link) => (
                <Button
                  key={link.path}
                  component={Link}
                  to={link.path}
                  color="inherit"
                  sx={{ 
                    mx: 1,
                    fontWeight: 500,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: location.pathname === link.path ? '100%' : '0%',
                      height: '2px',
                      bottom: 0,
                      left: 0,
                      backgroundColor: 'white',
                      transition: 'width 0.3s ease'
                    },
                    '&:hover::after': {
                      width: '100%'
                    }
                  }}
                >
                  {link.name}
                </Button>
              ))}


              {/* Language selector */}
              <IconButton
                color="inherit"
                onClick={handleLanguageMenuOpen}
                aria-label={translations.header.language}
                aria-controls="language-menu"
                aria-haspopup="true"
                sx={{ ml: 1 }}
              >
                <Language />
              </IconButton>
              <Menu
                id="language-menu"
                anchorEl={languageMenu}
                open={Boolean(languageMenu)}
                onClose={handleLanguageMenuClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: 2,
                    minWidth: 120
                  }
                }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={language === lang.code}
                    sx={{
                      fontWeight: language === lang.code ? 700 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                color="inherit"
                onClick={toggleMobileMenu}
                aria-label="Open menu"
                edge="end"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: '70%',
            maxWidth: 300,
            backgroundColor: '#1273D1',
            color: 'white'
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton color="inherit" onClick={toggleMobileMenu}>
            <Close />
          </IconButton>
        </Box>
        <List>
          {navLinks.map((link) => (
            <ListItem 
              button 
              key={link.path} 
              component={Link} 
              to={link.path}
              onClick={toggleMobileMenu}
              sx={{
                borderLeft: location.pathname === link.path ? '4px solid white' : 'none',
                backgroundColor: location.pathname === link.path 
                  ? 'rgba(0,0,0,0.1)'
                  : 'transparent'
              }}
            >
              <ListItemText primary={link.name} />
            </ListItem>
          ))}
          <ListItem button onClick={handleLanguageMenuOpen}>
            <ListItemText primary={translations.header.language} />
            <Language />
          </ListItem>
        </List>
      </Drawer>

      {/* Mobile Language Menu */}
      <Menu
        id="mobile-language-menu"
        anchorEl={languageMenu}
        open={Boolean(languageMenu)}
        onClose={handleLanguageMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 120
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={language === lang.code}
            sx={{
              fontWeight: language === lang.code ? 700 : 400
            }}
          >
            {lang.label}
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default ModernHeader;
